import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError, fmtDate, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { useFileViewer } from "@/components/FileViewer";
import { Download, AlertTriangle, CalendarClock, FlaskConical, FileWarning, BookOpenCheck } from "lucide-react";
import { ModuleNav } from "./ModuleNav";

const Bar = ({ percent }) => (
  <div className="h-2 w-full bg-slate-200">
    <div className={`h-2 ${percent >= 95 ? "bg-[#134e4a]" : percent >= 80 ? "bg-amber-500" : "bg-red-700"}`}
         style={{ width: `${percent}%` }} />
  </div>
);

const Tile = ({ label, value, tone = "text-slate-900", testid }) => (
  <div className="border border-slate-200 bg-white p-3" data-testid={testid}>
    <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
    <div className={`num mt-1 text-2xl font-bold ${tone}`}>{value}</div>
  </div>
);

export default function Readiness() {
  const [data, setData] = useState(null);
  const [binderOpen, setBinderOpen] = useState(false);
  const [sections, setSections] = useState([]);
  const [picked, setPicked] = useState([]);
  const [months, setMonths] = useState(24);
  const [building, setBuilding] = useState(false);
  const { openFile, viewerElement } = useFileViewer();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/accreditation/summary");
      setData(data);
    } catch (e) { toast.error(apiError(e)); }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get("/accreditation/binder/sections").then((r) => {
      setSections(r.data.sections);
      setPicked(r.data.sections.map((s) => s.key));
    }).catch(() => {});
  }, []);

  const toggle = (key) => setPicked((p) => (p.includes(key) ? p.filter((k) => k !== key) : [...p, key]));

  const buildBinder = async () => {
    if (picked.length === 0) return toast.error("Pick at least one section");
    setBuilding(true);
    try {
      await openFile(`/accreditation/binder.pdf?months=${months}&sections=${picked.join(",")}`, {
        filename: `PathQity_InspectionBinder_${months}mo.pdf`,
        title: "Inspection binder",
      });
      setBinderOpen(false);
    } finally { setBuilding(false); }
  };

  if (!data) return <div className="p-6 text-sm text-slate-500" data-testid="readiness-loading">Loading readiness…</div>;

  const o = data.overall;

  return (
    <div className="space-y-6" data-testid="readiness-page">
      <ModuleNav
        title="Inspection Readiness"
        blurb="Where the lab stands against its CAP checklist right now — compliance by department, what is overdue or at risk, proficiency testing failures and open corrective actions."
        actions={
          <>
            <Button variant="outline" className="h-11 border-slate-300" onClick={() => setBinderOpen(true)}
                    data-testid="binder-open-button">
              <BookOpenCheck size={16} className="mr-2" /> Inspection binder
            </Button>
            <Button asChild variant="outline" className="h-11 border-slate-300" data-testid="readiness-export-button">
              <a href={`${API}/accreditation/export.csv`}><Download size={16} className="mr-2" /> Export for inspection</a>
            </Button>
          </>
        }
      />

      <section className="border border-slate-200 bg-white p-4" data-testid="readiness-overall">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <p className="text-xs uppercase tracking-wide text-slate-500">Overall CAP compliance</p>
            <p className="num text-5xl font-bold text-[#134e4a]" data-testid="readiness-overall-percent">{o.percent}%</p>
          </div>
          <p className="text-sm text-slate-600">
            {o.compliant} of {o.scored} scored items compliant · {o.non_compliant} non-compliant ·{" "}
            {o.in_progress} in progress · {o.not_applicable} N/A
          </p>
        </div>
        <div className="mt-3"><Bar percent={o.percent} /></div>
      </section>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
        <Tile label="Items overdue" value={o.overdue} tone={o.overdue ? "text-red-800" : "text-slate-900"} testid="readiness-tile-overdue" />
        <Tile label={`At risk ≤${data.at_risk_days}d`} value={o.at_risk} tone={o.at_risk ? "text-amber-700" : "text-slate-900"} testid="readiness-tile-at-risk" />
        <Tile label="Non-compliant" value={o.non_compliant} tone={o.non_compliant ? "text-red-800" : "text-slate-900"} testid="readiness-tile-noncompliant" />
        <Tile label="PT failures" value={data.pt.failed} tone={data.pt.failed ? "text-red-800" : "text-slate-900"} testid="readiness-tile-pt-failed" />
        <Tile label="Open CAPAs" value={data.nonconformance.open + data.nonconformance.in_progress}
              tone={data.nonconformance.open ? "text-amber-700" : "text-slate-900"} testid="readiness-tile-open-nc" />
      </div>

      <section className="border border-slate-200" data-testid="readiness-departments">
        <header className="border-b border-slate-200 bg-slate-50 px-3 py-2">
          <h2 className="text-base font-semibold text-slate-900 md:text-lg">Compliance by department</h2>
        </header>
        {data.by_department.length === 0 ? (
          <div className="px-3 py-8 text-center text-sm text-slate-500" data-testid="readiness-no-departments">
            No checklist items yet. <Link to="/accreditation/checklist" className="text-[#134e4a] underline">Build the checklist</Link> to start scoring readiness.
          </div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {data.by_department.map((d) => (
              <li key={d.department} className="px-3 py-3" data-testid={`readiness-dept-${d.department.replace(/\s+/g, "-").toLowerCase()}`}>
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <Link to={`/accreditation/checklist?department=${encodeURIComponent(d.department)}`}
                        className="font-semibold text-[#134e4a] hover:underline">{d.department}</Link>
                  <span className="text-xs text-slate-500">{d.compliant}/{d.scored} compliant · {d.total} items</span>
                  {d.overdue > 0 && <span className="bg-red-800 px-1.5 py-0.5 text-[11px] font-semibold text-white">{d.overdue} overdue</span>}
                  {d.at_risk > 0 && <span className="bg-amber-100 px-1.5 py-0.5 text-[11px] font-semibold text-amber-900">{d.at_risk} at risk</span>}
                  <span className="num ml-auto text-lg font-bold text-slate-900">{d.percent}%</span>
                </div>
                <div className="mt-2"><Bar percent={d.percent} /></div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <div className="grid gap-4 lg:grid-cols-2">
        <section className="border border-slate-200" data-testid="readiness-upcoming">
          <header className="flex items-center gap-2 border-b border-slate-200 bg-slate-50 px-3 py-2">
            <CalendarClock size={15} className="text-[#134e4a]" />
            <h2 className="text-base font-semibold text-slate-900 md:text-lg">Overdue & upcoming due dates</h2>
          </header>
          {data.upcoming.length === 0 ? (
            <p className="px-3 py-8 text-center text-sm text-slate-500" data-testid="readiness-upcoming-empty">
              Nothing overdue or due in the next {data.at_risk_days} days.
            </p>
          ) : (
            <ul className="divide-y divide-slate-100">
              {data.upcoming.map((u, i) => (
                <li key={`${u.kind}-${i}`} className="flex flex-wrap items-center gap-2 px-3 py-2 text-sm"
                    data-testid={`readiness-upcoming-${i}`}>
                  <span className="bg-slate-100 px-1.5 py-0.5 text-[11px] font-semibold uppercase text-slate-700">{u.kind}</span>
                  <Link to={u.link} className="min-w-0 flex-1 truncate text-slate-800 hover:text-[#134e4a]">{u.label}</Link>
                  <span className={`text-xs font-semibold ${u.overdue ? "text-red-800" : "text-amber-800"}`}>
                    {u.overdue ? `${Math.abs(u.days_until_due)}d overdue` : `in ${u.days_until_due}d`}
                  </span>
                  <span className="text-xs text-slate-500">{fmtDate(u.due_date)}</span>
                </li>
              ))}
            </ul>
          )}
        </section>

        <div className="space-y-4">
          <section className="border border-slate-200" data-testid="readiness-noncompliant-list">
            <header className="flex items-center gap-2 border-b border-slate-200 bg-slate-50 px-3 py-2">
              <AlertTriangle size={15} className="text-red-800" />
              <h2 className="text-base font-semibold text-slate-900 md:text-lg">Non-compliant checklist items</h2>
            </header>
            {data.non_compliant_items.length === 0 ? (
              <p className="px-3 py-6 text-center text-sm text-slate-500">No non-compliant items on record.</p>
            ) : (
              <ul className="divide-y divide-slate-100">
                {data.non_compliant_items.map((i) => (
                  <li key={i.id} className="px-3 py-2 text-sm" data-testid={`readiness-nc-item-${i.id}`}>
                    <Link to="/accreditation/checklist" className="font-mono text-xs font-semibold text-[#134e4a] hover:underline">{i.reference}</Link>
                    <span className="ml-2 text-slate-700">{i.requirement.slice(0, 90)}</span>
                    <div className="text-[11px] text-slate-500">{i.department}{i.assigned_to ? ` · ${i.assigned_to}` : " · unassigned"}</div>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="border border-slate-200" data-testid="readiness-pt-failures">
            <header className="flex items-center gap-2 border-b border-slate-200 bg-slate-50 px-3 py-2">
              <FlaskConical size={15} className="text-[#134e4a]" />
              <h2 className="text-base font-semibold text-slate-900 md:text-lg">
                Proficiency testing — {data.pt.pending} pending · {data.pt.ca_outstanding} needing corrective action
              </h2>
            </header>
            {data.pt.failures.length === 0 ? (
              <p className="px-3 py-6 text-center text-sm text-slate-500">No unsatisfactory results on record.</p>
            ) : (
              <ul className="divide-y divide-slate-100">
                {data.pt.failures.map((e) => (
                  <li key={e.id} className="px-3 py-2 text-sm" data-testid={`readiness-pt-fail-${e.id}`}>
                    <Link to="/accreditation/pt" className="font-semibold text-[#134e4a] hover:underline">{e.analyte}</Link>
                    <span className="ml-2 bg-red-800 px-1.5 py-0.5 text-[11px] font-semibold text-white">{e.status}</span>
                    <div className="text-[11px] text-slate-500">{e.program} {e.survey} {e.survey_year || ""} · {e.department}</div>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="border border-slate-200" data-testid="readiness-open-nc">
            <header className="flex items-center gap-2 border-b border-slate-200 bg-slate-50 px-3 py-2">
              <FileWarning size={15} className="text-amber-800" />
              <h2 className="text-base font-semibold text-slate-900 md:text-lg">Open nonconformances</h2>
            </header>
            {data.nonconformance.open_list.length === 0 ? (
              <p className="px-3 py-6 text-center text-sm text-slate-500">Nothing open. All corrective actions closed.</p>
            ) : (
              <ul className="divide-y divide-slate-100">
                {data.nonconformance.open_list.map((n) => (
                  <li key={n.id} className="flex flex-wrap items-center gap-2 px-3 py-2 text-sm"
                      data-testid={`readiness-open-nc-${n.id}`}>
                    <Link to="/accreditation/nonconformance" className="font-mono text-xs font-semibold text-[#134e4a] hover:underline">{n.nc_number}</Link>
                    <span className="min-w-0 flex-1 truncate text-slate-800">{n.title}</span>
                    {n.overdue && <span className="bg-red-800 px-1.5 py-0.5 text-[11px] font-semibold text-white">overdue</span>}
                    <span className="text-[11px] text-slate-500">{n.status}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      </div>

      <Dialog open={binderOpen} onOpenChange={setBinderOpen}>
        <DialogContent className="max-h-[92vh] max-w-xl overflow-y-auto" data-testid="binder-dialog">
          <DialogHeader><DialogTitle className="font-display">Generate the inspection binder</DialogTitle>
            <DialogDescription>Pick the review period and the sections the inspector should see.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-600">Review period</Label>
              <select value={months} onChange={(e) => setMonths(Number(e.target.value))} data-testid="binder-months"
                      className="mt-1 h-11 w-full rounded-md border border-slate-300 bg-white px-3 text-sm">
                {[6, 12, 24, 36].map((m) => <option key={m} value={m}>Last {m} months</option>)}
              </select>
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label className="text-xs uppercase tracking-wide text-slate-600">Sections</Label>
                <button onClick={() => setPicked(picked.length === sections.length ? [] : sections.map((s) => s.key))}
                        data-testid="binder-toggle-all" className="text-xs font-semibold text-[#134e4a] hover:underline">
                  {picked.length === sections.length ? "Clear all" : "Select all"}
                </button>
              </div>
              <div className="mt-2 grid gap-1.5">
                {sections.map((s) => (
                  <label key={s.key} className="flex items-center gap-2 text-sm text-slate-700">
                    <input type="checkbox" checked={picked.includes(s.key)} onChange={() => toggle(s.key)}
                           data-testid={`binder-section-${s.key}`} className="h-4 w-4 accent-[#134e4a]" />
                    {s.label}
                  </label>
                ))}
              </div>
            </div>
            <Button onClick={buildBinder} disabled={building} data-testid="binder-generate"
                    className="h-11 w-full bg-[#134e4a] hover:bg-[#0f3f3c]">
              {building ? "Building the binder…" : "Generate PDF"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      {viewerElement}
    </div>
  );
}
